<template>
  <div class="review-fraud">
    <p>
      We detected potentially fraudulent activity on this card and temporarily locked it.
      Review the recent transactions below and confirm your activity.
    </p>

    <p class="review-fraud__question"><strong>Do you recognize all of these transactions?</strong></p>

    <div v-if="loading" class="review-fraud__loading">Loading transactions&hellip;</div>

    <div v-else-if="loadFailed" class="review-fraud__loading">
      Could not load recent transactions.
      <a href="javascript:;" @click="fetchTransactions">Try again</a>
    </div>

    <div v-else-if="transactions.length === 0" class="review-fraud__loading">
      No recent transactions to review. If you did not expect this card to be locked, please report it as lost or stolen below.
    </div>

    <ul v-else class="review-fraud__list">
      <li v-for="t in transactions" :key="t.id">
        {{ t.status }} transaction at {{ t.merchant || 'UNKNOWN' }}
        for <v-money currency="USD" locale="en">{{ t.amount }}</v-money>
        on {{ moment(t.date).format(dateFormat) }}
      </li>
    </ul>

    <div v-if="isEvedAdmin" class="review-fraud__admin-hint">
      As an Eved Admin you cannot resolve fraud locks here &mdash; use the
      <strong>Unlock Card</strong> action from the card&rsquo;s menu to record the required help desk ticket number.
    </div>

    <div v-if="!isEvedAdmin && busy" class="review-fraud__loading">Updating card&hellip;</div>

    <div v-if="!isEvedAdmin && !busy" class="review-fraud__actions">
      <ed-button
        button-class="btn btn-actionbar review-fraud__yes"
        :disabled="!canRecognize"
        @click="confirmRecognize">
        Yes, I recognize all these transactions
      </ed-button>
      <p class="review-fraud__hint">YES will make your card immediately ready for use again.</p>

      <ed-button
        button-class="btn btn-actionbar alt review-fraud__no"
        :disabled="busy"
        @click="reportLostOrStolen">
        No, I don't recognize one or more of these transactions
      </ed-button>
      <p class="review-fraud__hint">NO will report the card as lost or stolen.</p>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    card: { type: Object, required: true }
  },

  data() {
    return {
      transactions: [],
      loading: true,
      loadFailed: false,
      busy: false,
      dateFormat: 'MM/DD/YYYY'
    }
  },

  computed: {
    moment() { return global.moment },
    isEvedAdmin() {
      return !!(this.$user && this.$user.$ && this.$user.$.user && this.$user.$.user.IsEvedAdmin)
    },
    canRecognize() {
      // YES is a high-trust action — only enable when the cardholder has had the chance to see the transactions
      // they are vouching for. Hide it while loading, after a load failure, and when the list came back empty.
      return !this.busy && !this.loading && !this.loadFailed && this.transactions.length > 0
    }
  },

  created() {
    const formats = this.$user &&
      this.$user.sessionSettings &&
      this.$user.sessionSettings.DateAndTimeFormats
    if (formats && formats.MomentDateFormat) {
      this.dateFormat = formats.MomentDateFormat
    }
    this.fetchTransactions()
  },

  methods: {
    async fetchTransactions() {
      this.loading = true
      this.loadFailed = false
      try {
        // The grid endpoint expects `filter` as a JSON string (it Deserializes to Dictionary<string,string>).
        // Sending a plain object lets qs.stringify form-encode it as filter[CardId]=…, the server-side
        // CardId scoping then never applies, and the response contains the user's transactions across ALL
        // their cards — the client-side .filter below would silently return [] when this card's recent
        // activity falls outside the global top N.
        const response = await this.api.forms.post(this.api.$.evedpaycard.transactions, {
          sort: 'TransactionDate',
          dir: 'DESC',
          start: 0,
          // Must match the email — see GetWaitingPotentialFraudulentActivityEmails in
          // Eved/core/Eved.PayCards.Repositories/EvedPayCardsRepository.cs (Take(4)).
          // Cardholders compare this list against the email, so the counts must agree.
          limit: 4,
          isLiveGrid: true,
          filter: JSON.stringify({
            CardId: this.card.Id,
            searchTerm: ''
          })
        })
        const rows = (response && response.data && response.data.Data) || []
        this.transactions = rows
          .filter(t => t.CardId === this.card.Id)
          .map(t => ({
            id: t.Id,
            merchant: t.Merchant,
            amount: t.Amount,
            date: t.TransactionDate,
            status: t.TransactionStatus
          }))
      } catch (err) {
        this.loadFailed = true
        this.transactions = []
      } finally {
        this.loading = false
      }
    },

    confirmRecognize() {
      this.changeStatus('unlock', 'unlock')
    },

    reportLostOrStolen() {
      this.changeStatus('report-lost-or-stolen', 'lost-stolen')
    },

    async changeStatus(endpoint, successAction) {
      if (this.busy) return
      this.busy = true
      this.$emit('busy', true)
      try {
        // Axios rejects non-2xx, so reaching this line means the PATCH succeeded.
        // The global response interceptor surfaces error toasts on failures, so the modal does not duplicate them.
        await this.api.patch(`evedpaycards/${this.card.Id}/${endpoint}`)
        // Stay busy on the success branch so the modal keeps showing "Updating card…" through the parent's
        // follow-up GET. The parent will tear the modal down when refreshCardWithDetails resolves; releasing
        // busy here would re-enable the action buttons and unblock close on a stale state.
        this.$emit('success', successAction)
      } catch (err) {
        // Global interceptor already showed a toast; nothing further to do but re-enable the buttons.
        this.busy = false
        this.$emit('busy', false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@/assets/sass/_colors";
@import "~@/assets/sass/_variables";

.review-fraud {
  padding: 0.25rem 0.25rem 0;

  &__question {
    margin-top: 1rem;
  }

  &__loading {
    font-style: italic;
    color: #666;
    margin: 0.5rem 0;
  }

  &__list {
    margin: 0.25rem 0 1rem 1.25rem;
    padding: 0;
    li {
      margin-bottom: 0.25rem;
    }
  }

  &__admin-hint {
    background: #fff8e1;
    border: 1px solid #f0c674;
    color: #555;
    padding: 0.75rem;
    border-radius: 4px;
    margin: 1rem 0 0.25rem;
  }

  &__actions {
    margin-top: 1rem;
  }

  // ed-button renders its <button class="dawn-button"> at the component root with our button-class
  // merged onto it — review-fraud__yes/__no live on the same element as .btn / .btn-actionbar / .alt,
  // not as descendants. The chained .dawn-button bumps specificity above ed-button's own scoped
  // `.btn.btn.btn-actionbar` (and `.alt`) defaults, which would otherwise tie on specificity and
  // win by source order, leaving YES white and NO solid blue.

  // YES: solid blue affirmative action — Figma "Modal / EvedPay Card / Lock Card Activity" spec.
  &__yes.btn.btn-actionbar.dawn-button {
    width: 100%;
    margin-bottom: 0.25rem;
    background-color: $primary-alt;
    border-color: $primary-alt;
    color: $white-color;

    &:hover:not(:disabled),
    &:active:not(:disabled),
    &:focus:not(:disabled) {
      background-color: darken($primary-alt, 8%);
      border-color: darken($primary-alt, 8%);
      color: $white-color;
    }
  }

  // NO: outlined red destructive action — chains .alt and .dawn-button to beat .btn-actionbar.alt.
  &__no.btn.btn-actionbar.alt.dawn-button {
    width: 100%;
    margin-bottom: 0.25rem;
    background-color: transparent;
    border: 1px solid $urgent-02;
    color: $urgent-02;

    &:hover:not(:disabled),
    &:active:not(:disabled),
    &:focus:not(:disabled) {
      background-color: rgba($urgent-02, 0.08);
      border-color: $urgent-02;
      color: $urgent-02;
    }
  }

  &__hint {
    font-size: 0.875rem;
    color: #555;
    margin: 0 0 0.75rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-list/ReviewFraudActivity.vue